import { BreakpointObserver } from '@angular/cdk/layout';
import { AfterViewInit, Component, OnInit, ViewChild, signal, inject, OnDestroy, effect } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { QuillEditorComponent } from 'ngx-quill';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { ShareMenuDialogComponent } from 'src/app/shared/components/share-menu-dialog/share-menu-dialog.component';
import { sanitizeQuillText } from 'src/app/shared/helpers/utils';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { GroupsService } from 'src/app/views/groups/groups.service';
import { MySpaceService } from '../../my-space.service';
import { FinetuneExampleEditorComponent } from '../finetune-example-editor/finetune-example-editor.component';
import { ModelsService } from '../models.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';

interface AiModel {
  value: string;
  viewValue: string;
}
export interface TableRow {
  input: string;
  output: string;
}
@Component({
  selector: 'app-model-displayer',
  templateUrl: './model-displayer.component.html',
  styleUrls: ['./model-displayer.component.scss'],
  standalone: false,
})
export class ModelDisplayerComponent implements OnInit, AfterViewInit, OnDestroy {
  public sidenavService = inject(SidenavService);
  public isSmallScreen = this.sidenavService.isSmallOrTablet;
  @ViewChild('editor') editor: QuillEditorComponent;
  template;
  form;
  loading = false;
  fineTuningStatus = '';
  @ViewChild('paginator') paginator: MatPaginator;

  displayedColumns: string[] = ['input', 'output', 'buttons'];
  dataSource: MatTableDataSource<TableRow>;
  fineTuningExamples = [];
  isPlanFree = true;
  userID;
  public userGroups = JSON.parse(localStorage.getItem('userGroups') || '[]');

  canUndo = false;
  canRedo = false;
  currentHeaderLabel = 'Quill.Header.Normal';

  headerOptions = [
    { id: 1, text: 'Quill.Header.1', icon: 'format_h1' },
    { id: 2, text: 'Quill.Header.2', icon: 'format_h2' },
    { id: 3, text: 'Quill.Header.3', icon: 'format_h3' },
    { id: 4, text: 'Quill.Header.4', icon: 'format_h4' },
    { id: 5, text: 'Quill.Header.5', icon: 'format_h5' },
    { id: 6, text: 'Quill.Header.6', icon: 'format_h6' },
    { id: false, text: 'Quill.Header.Normal', icon: 'text_fields' },
  ];

  quillModules = {
    toolbar: '#displayer-toolbar',
    keyboard: {
      bindings: {
        tab: {
          key: 9,
          handler: () => true, // Default handling
        },
      },
    },
  };

  // AI Generation state
  aiSectionExpanded = signal(false);
  aiGenerating = signal(false);
  aiExampleText = signal('');
  aiInstructions = signal('');
  attachedFiles = signal<{ id: string; file: File; url?: string; name: string; uploading?: boolean; filename?: string }[]>([]);

  groups = [{ name: 'Global.None', _id: 'private' }];
  constructor(
    private aroute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private modelService: ModelsService,
    private toast: ToastService,
    private translate: TranslateService,
    private router: Router,
    private dialog: MatDialog,
    private confirm: AppConfirmService,
    private mySpaceService: MySpaceService,
    private groupService: GroupsService,
    private breakpointObserver: BreakpointObserver,
    private bottomSheet: MatBottomSheet,
    private authService: AuthService
  ) {
    effect(() => {
      const isSmall = this.isSmallScreen();
      this.sidenavService.showGlobalToggle.set(!isSmall);
    });
  }

  ngOnDestroy(): void {
    this.sidenavService.showGlobalToggle.set(true);
  }
  ngAfterViewInit(): void {
    this.dataSource = new MatTableDataSource();
    this.dataSource.data = this.fineTuningExamples;
    setTimeout(() => {
      this.dataSource.paginator = this.paginator;
    }, 2000);
    this.userID = this.authService.getID();
  }

  ngOnInit(): void {
    this.getTemplate();
    this.mySpaceService.isPlanFree().then((result) => {
      this.isPlanFree = !!result;
    });
    // this.getGroups();
  }
  getGroups() {
    this.groupService.getGroups().subscribe((groups) => {
      this.groups = [{ name: 'Global.None', _id: 'private' }];
      groups.forEach((group) => {
        this.groups.push(group);
      });
    });
  }
  generateUniqueString() {
    const timestamp = new Date().getTime().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 8);
    return timestamp + randomPart;
  }
  createFineTuningExample() {
    const example = {
      id: this.generateUniqueString(),
      input: '',
      output: '',
    };
    this.editExample(example, true);
    this.dataSource.data = this.fineTuningExamples;
  }

  editExample(example, isNew = false) {
    const dialogRef = this.dialog.open(FinetuneExampleEditorComponent, {
      disableClose: true,
      data: example,
      height: '70%',
    });
    firstValueFrom(dialogRef.afterClosed()).then((data) => {
      if (data.deleted) {
        this.fineTuningExamples = this.fineTuningExamples.filter((example) => example.id !== data.id);
        this.saveChanges();
      } else if (data.cancelled) {
        /* if (isNew) {

        } */
      } else {
        if (!isNew) {
          this.fineTuningExamples[this.fineTuningExamples.findIndex((example) => example.id === data.id)] = data;
        } else {
          this.fineTuningExamples.push(data);
        }
        this.dataSource.data = this.fineTuningExamples;
        this.saveChanges();
      }
    });
  }

  async removeExample(current) {
    const response = await firstValueFrom(this.confirm.confirm());
    if (response) {
      this.fineTuningExamples = this.fineTuningExamples.filter((example) => current.id !== example.id);
      this.dataSource.data = this.fineTuningExamples;
      this.saveChanges();
    }
  }

  dataIsEmpty() {
    const result = this.form.value?.data?.replace(/<[^>]*>?/gm, '').trim().length === 0;
    return result;
  }

  toggleAiSection() {
    this.aiSectionExpanded.update((v) => !v);
  }

  async onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = Date.now().toString() + '_' + i;
      this.attachedFiles.update((arr) => [...arr, { id: fileId, file, name: file.name, uploading: true }]);

      try {
        const folderId = this.template._id || 'temp_' + Date.now();
        const filename = `template_examples/${folderId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
        const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
        const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

        this.attachedFiles.update((arr) => arr.map((f) => (f.id === fileId ? { ...f, url: gsUrl, filename: filename, uploading: false } : f)));
      } catch (err) {
        console.error('File upload failed', err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
        this.attachedFiles.update((arr) => arr.filter((f) => f.id !== fileId));
      }
    }
    event.target.value = '';
  }

  removeAttachment(fileId: string) {
    const fileObj = this.attachedFiles().find((f) => f.id === fileId);
    if (fileObj && fileObj.filename) {
      this.authService.deleteFile(fileObj.filename).catch((e) => console.error('Cleanup failed', e));
    }
    this.attachedFiles.update((arr) => arr.filter((f) => f.id !== fileId));
  }

  generateWithAI() {
    const exampleText = this.aiExampleText();
    const instructions = this.aiInstructions();
    const files = this.attachedFiles()
      .filter((f) => !f.uploading && f.url)
      .map((f) => f.url);

    let currentTemplate = this.form.value.data;
    if (currentTemplate) {
      const stripped = currentTemplate.replace(/<[^>]*>/g, '').trim();
      if (!stripped) {
        currentTemplate = undefined; 
      }
    }

    if (!exampleText && !instructions && files.length === 0) return;

    this.aiGenerating.set(true);
    this.modelService
      .generateTemplateWithAI({
        exampleText,
        instructions,
        files,
        language: this.translate.currentLang,
        currentTemplate,
      })
      .subscribe({
        next: (res) => {
          this.aiGenerating.set(false);
          if (res.template) {
            this.form.controls['data'].setValue(res.template);
            this.aiSectionExpanded.set(false);
            this.toast.snackbar.open(this.translate.instant('Templates.AI.Success'), 'Ok', { duration: 3000 });
          }
        },
        error: (err) => {
          console.error(err);
          this.aiGenerating.set(false);
          this.toast.snackbar.open(this.translate.instant('Templates.AI.Error'), 'Ok', { duration: 3000 });
        },
      });
  }

  getTemplate() {
    this.loading = true;
    const id = this.aroute.snapshot.paramMap.get('id');
    this.modelService.getTemplate(id).subscribe({
      next: (res) => {
        if (!res) {
          this.router.navigate(['myspace', 'my-models']);
        }
        this.fineTuningExamples = res.fineTuningData?.examples || [];
        this.dataSource.data = this.fineTuningExamples;
        this.dataSource.paginator = this.paginator;
        this.template = res;

        this.form = this.fb.group({
          name: [res.name, [Validators.required, Validators.maxLength(100)]],
          data: [res.data, [Validators.required]],
          group: [res.group, [Validators.required]],
          keepFormatting: [res.keepFormatting || true],
        });
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
          duration: 2000,
        });
      },
    });
  }

  saveChanges() {
    return new Promise((resolve, reject) => {
      this.loading = true;
      const editObj = {
        _id: this.template._id,
        name: this.form.value.name,
        // aiModel: this.form.value.aiModel,
        data: sanitizeQuillText(this.form.value.data),
        group: this.form.value.group,
        fineTuningData: {
          examples: this.fineTuningExamples,
        },
        keepFormatting: this.form.value.keepFormatting,
      };
      this.modelService.editTemplate(editObj).subscribe({
        complete: () => {
          this.getTemplate();
          this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
            duration: 2000,
          });
          resolve({});
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
          reject();
        },
      });
    });
  }

  getStatusString(status) {
    if (status === 'succeeded') {
      return this.translate.instant('Templates.Status.Done');
    } else if (status === 'cancelled') {
      return this.translate.instant('Templates.Status.Cancelled');
    } else {
      return this.translate.instant('Templates.Status.Ongoing');
    }
  }

  async cancelFineTuningJob() {
    const response = await firstValueFrom(this.confirm.confirm());
    if (response) {
      this.modelService.cancelFineTuningJob({ id: this.template._id }).subscribe({
        complete: () => {
          this.getTemplate();
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
        },
      });
    }
  }

  async deleteFineTuning() {
    const response = await firstValueFrom(this.confirm.confirm());
    if (response) {
      this.modelService.deleteFineTunedModel({ id: this.template._id }).subscribe({
        complete: () => {
          this.getTemplate();
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
            duration: 2000,
          });
        },
      });
    }
  }
  openShareMenu() {
    const templateID = this.aroute.snapshot.paramMap.get('id');
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');

    if (isSmallScreen) {
      this.bottomSheet.open(ShareMenuDialogComponent, {
        data: {
          templateID,
        },
      });
    } else {
      this.dialog.open(ShareMenuDialogComponent, {
        width: '500px',
        maxHeight: '80vh',
        data: {
          templateID,
        },
      });
    }
  }

  updateHistoryState() {
    if (this.editor?.quillEditor?.history) {
      const stack = this.editor.quillEditor.history.stack;
      this.canUndo = stack.undo.length > 0;
      this.canRedo = stack.redo.length > 0;
    }
  }

  undo() {
    this.editor?.quillEditor?.history?.undo();
    this.updateHistoryState();
  }

  redo() {
    this.editor?.quillEditor?.history?.redo();
    this.updateHistoryState();
  }

  applyHeader(level: number | boolean) {
    this.editor.quillEditor.format('header', level, 'user');
    this.updateHeaderLabel(level);
  }

  clearFormatting() {
    const range = this.editor.quillEditor.getSelection();
    if (range) {
      this.editor.quillEditor.removeFormat(range.index, range.length, 'user');
      this.updateHeaderLabel(false); // Reset to Normal
    }
  }

  onContentChanged(event: any) {
    if (event.source === 'user') {
      this.updateHistoryState();
    }
  }

  onSelectionChanged(event: any) {
    if (event.range) {
      const format = this.editor.quillEditor.getFormat(event.range.index, event.range.length);
      const headerLevel = format.header || false;
      this.updateHeaderLabel(headerLevel);
      this.updateHistoryState();
    }
  }

  private updateHeaderLabel(level: any) {
    const found = this.headerOptions.find((opt) => opt.id === level);
    if (found) {
      this.currentHeaderLabel = found.text;
    } else {
      this.currentHeaderLabel = 'Quill.Header.Normal';
    }
  }
}
